import { createTheme, ThemeOptions } from '@mui/material/styles'

export const fontFamily = 'Helvetica Neue, Arial, sans-serif'

declare module '@mui/material/styles' {
    interface Palette {
        Blue25: string
        Blue50: string
        Blue100: string
        Blue200: string
        Blue300: string
        Blue400: string
        Blue500: string
        Blue600: string
        Blue700: string
        Blue800: string
        Blue900: string
        Grey50: string
        Grey100: string
        Grey200: string
        Grey250: string
        Grey300: string
        Grey400: string
        Grey500: string
        Grey600: string
        Grey700: string
        Grey800: string
        Grey850: string
        Grey900: string
        Black: string
        White: string
        Green50: string
        Green100: string
        Green200: string
        Green300: string
        Green400: string
        Green500: string
        Green600: string
        Green700: string
        Green800: string
        Green900: string
        Yellow50: string
        Yellow100: string
        Yellow200: string
        Yellow300: string
        Yellow400: string
        Yellow500: string
        Yellow600: string
        Yellow700: string
        Yellow800: string
        Yellow900: string
        Red50: string
        Red100: string
        Red200: string
        Red300: string
        Red400: string
        Red500: string
        Red600: string
        Red700: string
        Red800: string
        Red900: string
        BlackTransparent05: string
        BlackTransparent10: string
        BlackTransparent12: string
        BlackTransparent14: string
        BlackTransparent20: string
        BlackTransparent30: string
        BlackTransparent40: string
        BlackTransparent50: string
        BlackTransparent60: string
        BlackTransparent70: string
        BlackTransparent80: string
        BlackTransparent90: string
        WhiteTransparent05: string
        WhiteTransparent10: string
        WhiteTransparent12: string
        WhiteTransparent14: string
        WhiteTransparent20: string
        WhiteTransparent30: string
        WhiteTransparent40: string
        WhiteTransparent50: string
        WhiteTransparent60: string
        WhiteTransparent70: string
        WhiteTransparent80: string
        WhiteTransparent90: string
        Data100: string
        Data200: string
        Data300: string
        Data400: string
        Data500: string
        Data600: string
        Data700: string
        Data800: string
        Data900: string
        Data1000: string
        Data1100: string
        Data1200: string
        Data1300: string
        Data1400: string
        Data1500: string
        Data1600: string
        Data1700: string
        Data1800: string
        Data1900: string
        Data2000: string
        Data2100: string

        // --- Surfaces ---
        Surface100: string
        Surface200: string
        Surface300: string
        Surface400: string
        Surface500: string
        Surface600: string
        Surface700: string
        Surface800: string
        Surface900: string
        SurfaceInverse: string

        // --- Surface variants ---
        SurfaceDestructive100: string
        SurfaceDestructive300: string
        SurfaceDestructive500: string
        SurfaceError100: string
        SurfaceError300: string
        SurfaceError500: string
        SurfaceWarning100: string
        SurfaceWarning500: string
        SurfaceInfoBlue100: string
        SurfaceInfoBlue300: string
        SurfaceInfoBlue500: string
        SurfaceInfoGreen100: string
        SurfaceInfoGreen300: string
        SurfaceInfoGreen500: string

        // --- Text ---
        TextPrimary: string
        TextSecondary: string
        TextTertiary: string
        TextPlaceholder: string
        TextDisabled: string
        TextInverse: string
        TextDestructive: string
        TextError: string
        TextInfoBlue: string
        TextInfoGreen: string

        // --- Button (primary) ---
        ButtonPrimaryFilledBg: string
        ButtonPrimaryFilledBgHover: string
        ButtonPrimaryFilledBgActive: string
        ButtonPrimaryFilledBgDisabled: string
        ButtonPrimaryOutlinedBgHover: string
        ButtonPrimaryOutlinedBorder: string
        ButtonPrimaryOutlinedBorderHover: string
        ButtonPrimaryOutlinedBorderActive: string
        ButtonPrimaryOutlinedBorderDisabled: string

        // --- Buttons (floating/icon/secondary) ---
        ButtonFloatingBgHover: string
        ButtonFloatingBgActive: string
        ButtonIconBgHover: string
        ButtonIconBgActive: string
        ButtonSecondaryFilledBg: string
        ButtonSecondaryFilledBgHover: string
        ButtonSecondaryFilledBgActive: string
        ButtonSecondaryFilledBgDisabled: string
        ButtonSecondaryOutlinedBgHover: string
        ButtonSecondaryOutlinedBorder: string
        ButtonSecondaryOutlinedBorderHover: string
        ButtonSecondaryOutlinedBorderActive: string
        ButtonSecondaryOutlinedBorderDisabled: string

        // --- Inputs ---
        InputBg: string
        InputBgHover: string
        InputBgError: string
        InputBgErrorHover: string
        InputBgInferred: string
        InputBgInferredHover: string
        InputBgDisabled: string
        InputBorder: string
        InputBorderActive: string
        InputControlBgOff: string
        InputControlBgOffHover: string
        InputControlBgOffDisabled: string
        InputControlBgOn: string
        InputControlBgOnHover: string
        InputControlBgOnDisabled: string
        InputControlBorderOff: string
        InputControlBorderOn: string
        dataColors: {
            Data100: string
            Data200: string
            Data300: string
            Data400: string
            Data500: string
            Data600: string
            Data700: string
            Data800: string
            Data900: string
            Data1000: string
            Data1100: string
            Data1200: string
            Data1300: string
            Data1400: string
            Data1500: string
            Data1600: string
            Data1700: string
            Data1800: string
            Data1900: string
            Data2000: string
            Data2100: string
            DataQualityScoreA: string
            DataQualityScoreB: string
            DataQualityScoreC: string
            DataQualityScoreD: string
            defaultDataPalette: string[]
            smallDataPalette: string[]
        }
    }

    interface PaletteOptions extends Partial<Palette> {}

    interface TypographyVariants {
        body3: React.CSSProperties
        'body1-medium': React.CSSProperties
        'body2-medium': React.CSSProperties
        'body3-medium': React.CSSProperties
    }

    interface TypographyVariantsOptions {
        body3?: React.CSSProperties
        'body1-medium'?: React.CSSProperties
        'body2-medium'?: React.CSSProperties
        'body3-medium'?: React.CSSProperties
    }
}

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        body3: true
        'body1-medium': true
        'body2-medium': true
        'body3-medium': true
    }
}
const colors = {
    Black: '#000000',
    White: '#FFFFFF',

    Grey50: '#FAFAFA',
    Grey100: '#F5F5F5',
    Grey200: '#EEEEEE',
    Grey250: '#E8E8E8',
    Grey300: '#E0E0E0',
    Grey400: '#BDBDBD',
    Grey500: '#9E9E9E',
    Grey600: '#757575',
    Grey700: '#616161',
    Grey800: '#424242',
    Grey850: '#2B2B2B',
    Grey900: '#212121',

    Blue25: '#EFF5FF',
    Blue50: '#E3EDFF',
    Blue100: '#BFD1F4',
    Blue200: '#94B2EC',
    Blue300: '#6993E4',
    Blue400: '#487BDF',
    Blue500: '#2864D9',
    Blue600: '#245CD5',
    Blue700: '#1E52CF',
    Blue800: '#1848CA',
    Blue900: '#0F36C0',

    Green50: '#E1F1E6',
    Green100: '#B4DCC0',
    Green200: '#82C596',
    Green300: '#50AE6B',
    Green400: '#2B9C4C',
    Green500: '#058B2C',
    Green600: '#048327',
    Green700: '#047821',
    Green800: '#036E1B',
    Green900: '#015B10',

    Yellow50: '#FFFCEA',
    Yellow100: '#FFF7CA',
    Yellow200: '#FFF1A7',
    Yellow300: '#FFEB84',
    Yellow400: '#FFE769',
    Yellow500: '#FFE34F',
    Yellow600: '#FFE048',
    Yellow700: '#FFDC3F',
    Yellow800: '#FFD836',
    Yellow900: '#FFD026',

    Red50: '#FFEBEE',
    Red100: '#FFCDD2',
    Red200: '#EF9A9A',
    Red300: '#E57373',
    Red400: '#EF5350',
    Red500: '#F44336',
    Red600: '#D32F2F',
    Red700: '#C62828',
    Red800: '#B71C1C',
    Red900: '#911616',

    // Transparent blacks
    BlackTransparent05: 'rgba(0, 0, 0, 0.05)',
    BlackTransparent10: 'rgba(0, 0, 0, 0.10)',
    BlackTransparent12: 'rgba(0, 0, 0, 0.12)',
    BlackTransparent14: 'rgba(0, 0, 0, 0.14)',
    BlackTransparent20: 'rgba(0, 0, 0, 0.20)',
    BlackTransparent30: 'rgba(0, 0, 0, 0.30)',
    BlackTransparent40: 'rgba(0, 0, 0, 0.40)',
    BlackTransparent50: 'rgba(0, 0, 0, 0.50)',
    BlackTransparent60: 'rgba(0, 0, 0, 0.60)',
    BlackTransparent70: 'rgba(0, 0, 0, 0.70)',
    BlackTransparent80: 'rgba(0, 0, 0, 0.80)',
    BlackTransparent90: 'rgba(0, 0, 0, 0.90)',

    // Transparent whites
    WhiteTransparent05: 'rgba(255, 255, 255, 0.05)',
    WhiteTransparent10: 'rgba(255, 255, 255, 0.10)',
    WhiteTransparent12: 'rgba(255, 255, 255, 0.12)',
    WhiteTransparent14: 'rgba(255, 255, 255, 0.14)',
    WhiteTransparent20: 'rgba(255, 255, 255, 0.20)',
    WhiteTransparent30: 'rgba(255, 255, 255, 0.30)',
    WhiteTransparent40: 'rgba(255, 255, 255, 0.40)',
    WhiteTransparent50: 'rgba(255, 255, 255, 0.50)',
    WhiteTransparent60: 'rgba(255, 255, 255, 0.60)',
    WhiteTransparent70: 'rgba(255, 255, 255, 0.70)',
    WhiteTransparent80: 'rgba(255, 255, 255, 0.80)',
    WhiteTransparent90: 'rgba(255, 255, 255, 0.90)',

    // Data colors
    Data100: '#0099F1',
    Data200: '#31B5FF',
    Data300: '#3BC9F0',
    Data400: '#3EDADA',
    Data500: '#69DFC9',
    Data600: '#A0EB9E',
    Data700: '#C4EB9E',
    Data800: '#FDF07D',
    Data900: '#FFE175',
    Data1000: '#FFB967',
    Data1100: '#FF9559',
    Data1200: '#FF7759',
    Data1300: '#FF4343',
    Data1400: '#BDBDBD',
    Data1500: '#FF627F',
    Data1600: '#FF75AF',
    Data1700: '#FF8BD8',
    Data1800: '#E57EFF',
    Data1900: '#B77EFF',
    Data2000: '#787DF8',
    Data2100: '#0075FF',
} as const

const themeOptions: ThemeOptions = {
    spacing: 8,
    breakpoints: {
        keys: ['xs', 'sm', 'md', 'lg', 'xl'],
        values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
    },
    direction: 'ltr',
    palette: {
        ...colors,

        // --- Surfaces ---
        Surface100: colors.White,
        Surface200: colors.Grey50,
        Surface300: colors.Grey100,
        Surface400: colors.Grey200,
        Surface500: colors.Grey250,
        Surface600: colors.Grey300,
        Surface700: colors.Grey400,
        Surface800: colors.Grey600,
        Surface900: colors.Grey800,
        SurfaceInverse: colors.Grey900,

        // --- Surface variants ---
        SurfaceDestructive100: colors.Red50,
        SurfaceDestructive300: colors.Red100,
        SurfaceDestructive500: colors.Red700,
        SurfaceError100: colors.Red50,
        SurfaceError300: colors.Red100,
        SurfaceError500: colors.Red700,
        SurfaceWarning100: colors.Yellow100,
        SurfaceWarning500: colors.Yellow900,
        SurfaceInfoBlue100: colors.Blue25,
        SurfaceInfoBlue300: colors.Blue50,
        SurfaceInfoBlue500: colors.Blue500,
        SurfaceInfoGreen100: colors.Green50,
        SurfaceInfoGreen300: colors.Green100,
        SurfaceInfoGreen500: colors.Green700,

        // --- Text ---
        TextPrimary: colors.Grey900,
        TextSecondary: colors.Grey700,
        TextTertiary: colors.Grey500,
        TextPlaceholder: colors.Grey400,
        TextDisabled: colors.Grey400,
        TextInverse: colors.Grey50,
        TextDestructive: colors.Red700,
        TextError: colors.Red700,
        TextInfoBlue: colors.Blue500,
        TextInfoGreen: colors.Green800,

        // --- Button primary ---
        ButtonPrimaryFilledBg: colors.Grey900,
        ButtonPrimaryFilledBgHover: colors.Grey800,
        ButtonPrimaryFilledBgActive: colors.Black,
        ButtonPrimaryFilledBgDisabled: colors.Grey300,

        ButtonPrimaryOutlinedBgHover: colors.Grey200,
        ButtonPrimaryOutlinedBorder: colors.Grey800,
        ButtonPrimaryOutlinedBorderHover: colors.Grey600,
        ButtonPrimaryOutlinedBorderActive: colors.Grey900,
        ButtonPrimaryOutlinedBorderDisabled: colors.Grey300,

        ButtonFloatingBgHover: colors.BlackTransparent10,
        ButtonFloatingBgActive: colors.BlackTransparent12,
        ButtonIconBgHover: colors.BlackTransparent10,
        ButtonIconBgActive: colors.BlackTransparent12,

        // --- Button secondary ---
        ButtonSecondaryFilledBg: colors.Grey200,
        ButtonSecondaryFilledBgHover: colors.Grey300,
        ButtonSecondaryFilledBgActive: colors.Grey800,
        ButtonSecondaryFilledBgDisabled: colors.Grey300,
        ButtonSecondaryOutlinedBgHover: colors.Grey200,
        ButtonSecondaryOutlinedBorder: colors.Grey400,
        ButtonSecondaryOutlinedBorderHover: colors.Grey600,
        ButtonSecondaryOutlinedBorderActive: colors.Grey900,
        ButtonSecondaryOutlinedBorderDisabled: colors.Grey300,

        // --- Inputs ---
        InputBg: colors.White,
        InputBgHover: colors.Grey100,
        InputBgError: colors.Red50,
        InputBgErrorHover: colors.Red100,
        InputBgInferred: colors.Blue25,
        InputBgInferredHover: colors.Blue50,
        InputBgDisabled: colors.Grey100,
        InputBorder: colors.Grey400,
        InputBorderActive: colors.Grey600,

        InputControlBgOff: colors.Grey400,
        InputControlBgOffHover: colors.Grey250,
        InputControlBgOffDisabled: colors.Grey400,
        InputControlBgOn: colors.Grey900,
        InputControlBgOnHover: colors.Grey300,
        InputControlBgOnDisabled: colors.Grey800,
        InputControlBorderOff: colors.Grey600,
        InputControlBorderOn: colors.Grey900,

        dataColors: {
            Data100: colors.Data100,
            Data200: colors.Data200,
            Data300: colors.Data300,
            Data400: colors.Data400,
            Data500: colors.Data500,
            Data600: colors.Data600,
            Data700: colors.Data700,
            Data800: colors.Data800,
            Data900: colors.Data900,
            Data1000: colors.Data1000,
            Data1100: colors.Data1100,
            Data1200: colors.Data1200,
            Data1300: colors.Data1300,
            Data1400: colors.Data1400,
            Data1500: colors.Data1500,
            Data1600: colors.Data1600,
            Data1700: colors.Data1700,
            Data1800: colors.Data1800,
            Data1900: colors.Data1900,
            Data2000: colors.Data2000,
            Data2100: colors.Data2100,
            DataQualityScoreA: colors.Data500,
            DataQualityScoreB: colors.Data100,
            DataQualityScoreC: colors.Data900,
            DataQualityScoreD: colors.Data1000,
            get smallDataPalette() {
                return [
                    this.Data100,
                    this.Data200,
                    this.Data300,
                    this.Data400,
                    this.Data500,
                    this.Data600,
                    this.Data700,
                    this.Data900,
                    this.Data1000,
                    this.Data1400,
                    this.Data1500,
                ]
            },
            get defaultDataPalette() {
                return [
                    this.Data100,
                    this.Data200,
                    this.Data300,
                    this.Data400,
                    this.Data500,
                    this.Data600,
                    this.Data700,
                    this.Data800,
                    this.Data900,
                    this.Data1000,
                    this.Data1100,
                    this.Data1200,
                    this.Data1300,
                    this.Data1400,
                    this.Data1500,
                    this.Data1600,
                    this.Data1700,
                    this.Data1800,
                    this.Data1900,
                    this.Data2000,
                    this.Data2100,
                ]
            },
        },
    },
    typography: {
        fontFamily,
        fontSize: 14,
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        h1: {
            fontFamily,
            fontSize: '96px',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: '115.2px',
            letterSpacing: '0',
            textAlign: 'left',
            color: colors.Grey900,
        },
        h2: {
            fontFamily,
            fontSize: '64px',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: '76.8px',
            letterSpacing: '0',
            textAlign: 'left',
            color: colors.Grey900,
        },
        h3: {
            fontFamily,
            fontSize: '48px',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: '57.6px',
            letterSpacing: '0',
            textAlign: 'left',
            color: colors.Grey900,
        },
        h4: {
            fontFamily,
            fontSize: '36px',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: '43.2px',
            letterSpacing: '0',
            textAlign: 'left',
            color: colors.Grey900,
        },
        h5: {
            fontFamily,
            fontSize: '24px',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: '28.8px',
            letterSpacing: '0',
            textAlign: 'left',
            color: colors.Grey900,
        },
        h6: {
            fontFamily,
            fontSize: '18px',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: '21.6px',
            letterSpacing: '0',
            textAlign: 'left',
            color: colors.Grey900,
        },
        body1: {
            fontFamily,
            fontSize: '18px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '27px',
            letterSpacing: '0',
            textAlign: 'left',
            color: colors.Grey900,
        },
        body2: {
            fontFamily,
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '24px',
            letterSpacing: '0',
            textAlign: 'left',
            color: colors.Grey900,
        },
        body3: {
            fontFamily,
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '21px',
            letterSpacing: '0',
            textAlign: 'left',
            color: colors.Grey900,
        },
        'body1-medium': {
            fontFamily,
            fontSize: '18px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '27px',
            letterSpacing: '0',
            textAlign: 'left',
            color: colors.Grey900,
        },
        'body2-medium': {
            fontFamily,
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '24px',
            letterSpacing: '0',
            textAlign: 'left',
            color: colors.Grey900,
        },
        'body3-medium': {
            fontFamily,
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '21px',
            letterSpacing: '0',
            textAlign: 'left',
            color: colors.Grey900,
        },
        button: {
            fontFamily,
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '16.8px',
            letterSpacing: '0',
            textTransform: 'none',
        },
        caption: {
            fontFamily,
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '18px',
            letterSpacing: '0',
            textAlign: 'left',
        },
    },
    shadows: [
        'none',
        `0px 2px 1px -1px ${colors.BlackTransparent20}, 0px 1px 1px 0px ${colors.BlackTransparent14}, 0px 1px 3px 0px ${colors.BlackTransparent12}`,
        `0px 3px 1px -2px ${colors.BlackTransparent20}, 0px 2px 2px 0px ${colors.BlackTransparent14}, 0px 1px 5px 0px ${colors.BlackTransparent12}`,
        `0px 3px 3px -2px ${colors.BlackTransparent20}, 0px 3px 4px 0px ${colors.BlackTransparent14}, 0px 1px 8px 0px ${colors.BlackTransparent12}`,
        `0px 1px 10px 0px ${colors.BlackTransparent12}, 0px 4px 5px 0px ${colors.BlackTransparent14}, 0px 2px 4px -1px ${colors.BlackTransparent20}`,
        `0px 3px 5px -1px ${colors.BlackTransparent20}, 0px 5px 8px 0px ${colors.BlackTransparent14}, 0px 1px 14px 0px ${colors.BlackTransparent12}`,
        `0px 1px 18px 0px ${colors.BlackTransparent12}, 0px 6px 10px 0px ${colors.BlackTransparent14}, 0px 3px 5px -1px ${colors.BlackTransparent20}`,
        `0px 4px 5px -2px ${colors.BlackTransparent20}, 0px 7px 10px 1px ${colors.BlackTransparent14}, 0px 2px 16px 1px ${colors.BlackTransparent12}`,
        `0px 6px 30px 5px ${colors.BlackTransparent12}, 0px 16px 24px 2px ${colors.BlackTransparent14}, 0px 8px 10px -5px ${colors.BlackTransparent50}`,
        `0px 5px 6px -3px ${colors.BlackTransparent20}, 0px 9px 12px 1px ${colors.BlackTransparent14}, 0px 3px 16px 2px ${colors.BlackTransparent12}`,
        `0px 6px 6px -3px ${colors.BlackTransparent20}, 0px 10px 14px 1px ${colors.BlackTransparent14}, 0px 4px 18px 3px ${colors.BlackTransparent12}`,
        `0px 6px 7px -4px ${colors.BlackTransparent20}, 0px 11px 15px 1px ${colors.BlackTransparent14}, 0px 4px 20px 3px ${colors.BlackTransparent12}`,
        `0px 7px 8px -4px ${colors.BlackTransparent20}, 0px 12px 17px 2px ${colors.BlackTransparent14}, 0px 5px 22px 4px ${colors.BlackTransparent12}`,
        `0px 7px 8px -4px ${colors.BlackTransparent20}, 0px 13px 19px 2px ${colors.BlackTransparent14}, 0px 5px 24px 4px ${colors.BlackTransparent12}`,
        `0px 7px 9px -4px ${colors.BlackTransparent20}, 0px 14px 21px 2px ${colors.BlackTransparent14}, 0px 5px 26px 4px ${colors.BlackTransparent12}`,
        `0px 8px 9px -5px ${colors.BlackTransparent20}, 0px 15px 22px 2px ${colors.BlackTransparent14}, 0px 6px 28px 5px ${colors.BlackTransparent12}`,
        `0px 8px 10px -5px ${colors.BlackTransparent20}, 0px 16px 24px 2px ${colors.BlackTransparent14}, 0px 6px 30px 5px ${colors.BlackTransparent12}`,
        `0px 8px 11px -5px ${colors.BlackTransparent20}, 0px 17px 26px 2px ${colors.BlackTransparent14},0px 6px 32px 5px ${colors.BlackTransparent12}`,
        `0px 9px 11px -5px ${colors.BlackTransparent20}, 0px 18px 28px 2px ${colors.BlackTransparent14}, 0px 7px 34px 6px ${colors.BlackTransparent12}`,
        `0px 9px 12px -6px ${colors.BlackTransparent20}, 0px 19px 29px 2px ${colors.BlackTransparent14}, 0px 7px 36px 6px ${colors.BlackTransparent12}`,
        `0px 10px 13px -6px ${colors.BlackTransparent20}, 0px 20px 31px 3px ${colors.BlackTransparent14}, 0px 8px 38px 7px ${colors.BlackTransparent12}`,
        `0px 10px 13px -6px ${colors.BlackTransparent20}, 0px 21px 33px 3px ${colors.BlackTransparent14}, 0px 8px 40px 7px ${colors.BlackTransparent12}`,
        `0px 10px 14px -6px ${colors.BlackTransparent20}, 0px 22px 35px 3px ${colors.BlackTransparent14}, 0px 8px 42px 7px ${colors.BlackTransparent12}`,
        `0px 11px 14px -7px ${colors.BlackTransparent20}, 0px 23px 36px 3px ${colors.BlackTransparent14}, 0px 9px 44px 8px ${colors.BlackTransparent12}`,
        `0px 11px 15px -7px ${colors.BlackTransparent20}, 0px 24px 38px 3px ${colors.BlackTransparent14}, 0px 9px 46px 8px ${colors.BlackTransparent12}`,
    ],
}

const themeNew = createTheme(themeOptions)
export default themeNew
